import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Clock, Building2, User, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegistroPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [planos, setPlanos] = useState([]);
  const [error, setError] = useState('');
  
  const [empresaData, setEmpresaData] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cnae: '',
    endereco: '',
    telefone: '',
    email: '',
    plano_id: ''
  });

  const [usuarioData, setUsuarioData] = useState({
    nome: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefone: ''
  });

  useEffect(() => {
    fetchPlanos();
  }, []);

  const fetchPlanos = async () => {
    try {
      const response = await axios.get(`${API_URL}/planos`);
      setPlanos(response.data);
    } catch (error) {
      console.error('Error fetching planos:', error);
    }
  };

  const validateStep1 = () => {
    if (!empresaData.razao_social || !empresaData.nome_fantasia || !empresaData.cnpj || !empresaData.cnae || !empresaData.email) {
      setError('Preencha todos os campos obrigatórios');
      return false;
    }
    // Basic CNPJ validation (14 digits)
    const cnpjClean = empresaData.cnpj.replace(/\D/g, '');
    if (cnpjClean.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!usuarioData.nome || !usuarioData.email || !usuarioData.password) {
      setError('Preencha todos os campos obrigatórios');
      return false;
    }
    if (usuarioData.password !== usuarioData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    if (usuarioData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    setError('');
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setLoading(true);
    setError('');

    try {
      // Create the access request
      await axios.post(`${API_URL}/solicitacoes-acesso`, {
        empresa: empresaData,
        usuario: {
          nome: usuarioData.nome,
          email: usuarioData.email,
          password: usuarioData.password,
          telefone: usuarioData.telefone
        }
      });

      setSuccess(true);
      toast.success('Solicitação enviada com sucesso!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar solicitação');
      toast.error(err.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-xl animate-slide-up">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-3">
              Solicitação Enviada!
            </h2>
            <p className="text-muted-foreground mb-6">
              Sua solicitação de acesso foi enviada com sucesso. 
              Nossa equipe irá analisar e você receberá um e-mail quando sua conta for aprovada.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-muted-foreground mb-2">
                <strong>Empresa:</strong> {empresaData.razao_social}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>E-mail:</strong> {usuarioData.email}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-accent hover:bg-accent/90"
            >
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl border-slate-200 shadow-xl animate-slide-up">
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-primary font-['Outfit']">Jornyx</span>
            </Link>
          </div>
          <CardTitle className="text-2xl font-bold font-['Outfit']">Solicitar Acesso</CardTitle>
          <CardDescription>
            {step === 1 
              ? 'Preencha os dados da sua empresa para solicitar acesso à plataforma'
              : 'Agora preencha os dados do administrador da empresa'
            }
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2 pt-4">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${step >= 1 ? 'bg-accent text-white' : 'bg-slate-200 text-slate-500'}`}>
              1
            </div>
            <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-accent' : 'bg-slate-200'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${step >= 2 ? 'bg-accent text-white' : 'bg-slate-200 text-slate-500'}`}>
              2
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg flex items-center gap-2 mb-4">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Building2 className="w-4 h-4" />
                Dados da Empresa
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={empresaData.razao_social}
                    onChange={(e) => setEmpresaData({...empresaData, razao_social: e.target.value})}
                    placeholder="Razão Social da Empresa"
                    data-testid="input-razao-social"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nome_fantasia">Nome Fantasia *</Label>
                  <Input
                    id="nome_fantasia"
                    value={empresaData.nome_fantasia}
                    onChange={(e) => setEmpresaData({...empresaData, nome_fantasia: e.target.value})}
                    placeholder="Nome Fantasia"
                    data-testid="input-nome-fantasia"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    value={empresaData.cnpj}
                    onChange={(e) => setEmpresaData({...empresaData, cnpj: e.target.value})}
                    placeholder="00.000.000/0000-00"
                    data-testid="input-cnpj"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnae">CNAE *</Label>
                  <Input
                    id="cnae"
                    value={empresaData.cnae}
                    onChange={(e) => setEmpresaData({...empresaData, cnae: e.target.value})}
                    placeholder="0000-0/00"
                    data-testid="input-cnae"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_empresa">E-mail da Empresa *</Label>
                  <Input
                    id="email_empresa"
                    type="email"
                    value={empresaData.email}
                    onChange={(e) => setEmpresaData({...empresaData, email: e.target.value})}
                    placeholder="contato@empresa.com.br"
                    data-testid="input-email-empresa"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone_empresa">Telefone</Label>
                  <Input
                    id="telefone_empresa"
                    value={empresaData.telefone}
                    onChange={(e) => setEmpresaData({...empresaData, telefone: e.target.value})}
                    placeholder="(00) 0000-0000"
                    data-testid="input-telefone-empresa"
                  />
                </div>
                <div className="col-span-full space-y-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    value={empresaData.endereco}
                    onChange={(e) => setEmpresaData({...empresaData, endereco: e.target.value})}
                    placeholder="Endereço completo"
                    data-testid="input-endereco"
                  />
                </div>
                <div className="col-span-full space-y-2">
                  <Label htmlFor="plano">Plano Desejado</Label>
                  <Select
                    value={empresaData.plano_id}
                    onValueChange={(value) => setEmpresaData({...empresaData, plano_id: value})}
                  >
                    <SelectTrigger data-testid="select-plano">
                      <SelectValue placeholder="Selecione um plano (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {planos.map(plano => (
                        <SelectItem key={plano.id} value={plano.id}>
                          {plano.nome} - R$ {plano.valor_mensal}/mês ({plano.limite_empregados} empregados)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button
                  type="button"
                  onClick={handleNextStep}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="btn-next-step"
                >
                  Próximo
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <User className="w-4 h-4" />
                Dados do Administrador
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-full space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    value={usuarioData.nome}
                    onChange={(e) => setUsuarioData({...usuarioData, nome: e.target.value})}
                    placeholder="Seu nome completo"
                    data-testid="input-nome-usuario"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_usuario">E-mail *</Label>
                  <Input
                    id="email_usuario"
                    type="email"
                    value={usuarioData.email}
                    onChange={(e) => setUsuarioData({...usuarioData, email: e.target.value})}
                    placeholder="seu@email.com"
                    data-testid="input-email-usuario"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone_usuario">Telefone</Label>
                  <Input
                    id="telefone_usuario"
                    value={usuarioData.telefone}
                    onChange={(e) => setUsuarioData({...usuarioData, telefone: e.target.value})}
                    placeholder="(00) 00000-0000"
                    data-testid="input-telefone-usuario"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={usuarioData.password}
                    onChange={(e) => setUsuarioData({...usuarioData, password: e.target.value})}
                    placeholder="Mínimo 6 caracteres"
                    data-testid="input-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Senha *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={usuarioData.confirmPassword}
                    onChange={(e) => setUsuarioData({...usuarioData, confirmPassword: e.target.value})}
                    placeholder="Repita a senha"
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Importante:</strong> Após o envio, sua solicitação será analisada pela equipe Jornyx. 
                  Você receberá um e-mail quando sua conta for aprovada e poderá então fazer login.
                </p>
              </div>

              <div className="flex justify-between pt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="btn-submit"
                >
                  {loading ? 'Enviando...' : 'Enviar Solicitação'}
                </Button>
              </div>
            </form>
          )}

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-muted-foreground">
              Já tem uma conta?{' '}
              <Link to="/login" className="text-accent hover:underline font-medium">
                Fazer login
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistroPage;
